import React, { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Form, Alert, Button } from "antd";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";

import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";

import { useAuth } from "../../context/AuthContext";
import routeNames from "../../constants/route-names";
import regex from "../../constants/regex";

interface LoginFormValues {
  email: string;
  password: string;
}

export default function LogIn() {
  const [displayLoginError, setDisplayLoginError] = useState(false);
  const [form] = Form.useForm();

  const { loggedUserData, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loggedUserData) {
      const isAdmin = loggedUserData.isHighnoteAdmin;
      navigate(isAdmin ? routeNames.AI_CHATS : routeNames.AI_CHATS_NONADMIN);
    }
  }, [loggedUserData, navigate]);

  const handleLogin = useCallback(
    async (values: LoginFormValues) => {
      if (login) {
        console.log("handleLogin: ");
        setDisplayLoginError(false); // Reset error state

        const successLogin = await login({
          email: values.email,
          password: values.password,
        });

        if (!successLogin) {
          setDisplayLoginError(true);
        }
      }
    },
    [login]
  );

  const handleLoginClick = () => {
    form
      .validateFields()
      .then((values) => {
        handleLogin(values);
      })
      .catch((errorInfo) => {
        console.log("Validation failed:", errorInfo);
      });
  };

  return (
    <Container component="main" maxWidth={false} className="login-container">
      <CssBaseline />
      <div className="login-logo-container">
        <img
          src={
            process.env.PUBLIC_URL + "/assets/images/highnotelogo-gradient.svg"
          }
          className="login-logo"
          alt="Highnote Logo"
        />
        <span className="login-logo-text">Highnote</span>
        <span className="login-logo-description">
          It pays to pay it forward.
        </span>
      </div>

      <div className="login-form-container">
        <span className="login-form-description">
          Sign in with your Highnote account
        </span>

        <Form
          form={form}
          name="loginForm"
          layout="vertical"
          onFinish={handleLogin}
          autoComplete="off"
        >
          <Form.Item
            name="email"
            className="login-form-item"
            rules={[
              { required: true, message: "Please input your Email." },
              {
                pattern: regex.email,
                message: "Please provide a valid email address",
              },
            ]}
            validateTrigger="onBlur"
          >
            <Input
              placeholder="Email"
              className="app-input"
              autoComplete="off"
              allowClear
            />
          </Form.Item>

          <Form.Item
            name="password"
            className="login-form-item"
            rules={[{ required: true, message: "Please input a password." }]}
            validateTrigger="onBlur"
          >
            <Input.Password
              placeholder="Password"
              className="app-input"
              autoComplete="new-password"
              iconRender={(visible) =>
                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
              }
            />
          </Form.Item>

          {displayLoginError && (
            <Alert
              message="The email or password is incorrect."
              className="login-error"
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <button
            // type="button"
            onClick={handleLoginClick}
            className="sign-in-button"
          >
            Sign In
          </button>
        </Form>
        <span className="app-text">
          To create a new account, please download the Highnote mobile app for
          iOS or Android.
        </span>
        <div className="app-logos-container">
          <a href="https://apps.apple.com/us/app/highnote/id1667611270">
            <img
              src={process.env.PUBLIC_URL + "/assets/images/appstore-logo.svg"}
              className="login-logo"
              alt="AppStore Logo"
            />
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.highnote.android.app&hl=en_US">
            <img
              src={
                process.env.PUBLIC_URL + "/assets/images/googleplay-logo.svg"
              }
              className="login-logo"
              alt="Googleplay Logo"
            />
          </a>
        </div>
      </div>
    </Container>
  );
}
